import { useEffect } from "react"

const SITE_URL = "https://www.vszielarz.pl"

type SeoOptions = {
  /** Full <title> contents. */
  title: string
  /** Meta description. */
  description: string
  /** Path part of the canonical URL, e.g. "/smaki". Defaults to "/". */
  path?: string
  /** Optional og:image override (absolute URL). */
  image?: string
}

function upsertMeta(
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string
) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute("content", content)
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement("link")
    el.setAttribute("rel", "canonical")
    document.head.appendChild(el)
  }
  el.setAttribute("href", href)
}

/**
 * Imperatively manages document head tags for the current route. Because it
 * updates existing tags in place (rather than appending), it avoids duplicate
 * <title>/<meta>/<link> entries when navigating between routes in this SPA.
 */
export function useSeo({ title, description, path = "/", image }: SeoOptions) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path === "/" ? "/" : path}`

    document.title = title
    upsertMeta('meta[name="description"]', "name", "description", description)
    upsertMeta('meta[property="og:title"]', "property", "og:title", title)
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description
    )
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical)
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title)
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description
    )
    if (image) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", image)
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image)
    }
    upsertCanonical(canonical)
  }, [title, description, path, image])
}
